<template>
    <div class="stammdaten-container">
        <v-container fluid class="form-content overflow-auto pa-5">
            <v-form ref="form" :value="value" @input="$emit('input', $event)">
                <div class="d-flex align-center">
                    <h4 class="mb-2">Infos</h4>
                    <v-spacer />
                    <PublishedIndicator :record="record" />
                </div>
                <!-- Legal Type -->
                <v-row v-if="eventOrganizerRec" dense>
                    <v-col cols="12">
                        <v-radio-group
                            dense
                            :value="record.legal_type"
                            :rules="[rules.required]"
                            row
                            @change="onInput('legal_type', $event)"
                        >
                            <v-radio value="person">
                                <template #label>
                                    <KpIcon icon-cls="person" color="grey" />
                                    <span>{{ $t('person_label') }}</span>
                                </template>
                            </v-radio>

                            <v-radio value="organization">
                                <template #label>
                                    <KpIcon icon-cls="institution" color="grey" />
                                    <span>{{ $t('organization_label') }}</span>
                                </template>
                            </v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>

                <!-- Bezeichnung -->
                <v-row dense>
                    <v-col cols="12">
                        <LabelField
                            :label="record.legal_type === 'person' ? $t('bezeichnung_person') : $t('bezeichnung')"
                        >
                            <v-text-field
                                :value="record.title"
                                dense
                                outlined
                                hide-details="auto"
                                placeholder="Wondergirl, Superman, ..."
                                :counter="60"
                                :rules="
                                    [
                                        record.legal_type === 'organization' ? rules.required : null,
                                        rules.maxLength(60),
                                    ].filter(Boolean)
                                "
                                @input="onInput('title', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <!-- Geschlecht -->
                <v-row v-if="record.legal_type === 'person'" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('anrede')">
                            <v-radio-group dense :value="record.sex" row @change="onInput('sex', $event)">
                                <v-radio :label="$t('frau')" value="f" />
                                <v-radio :label="$t('herr')" value="m" />
                                <v-radio :label="$t('divers')" value="d" />
                            </v-radio-group>
                        </LabelField>
                    </v-col>
                </v-row>
                <!-- Vor-/Nachname -->
                <v-row v-if="record.legal_type === 'person'" dense>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('vorname')">
                            <v-text-field
                                :value="record.first_name"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.required, rules.maxLength(100)]"
                                @input="onInput('first_name', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('nachname')">
                            <v-text-field
                                :value="record.surname"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.required, rules.maxLength(100)]"
                                @input="onInput('surname', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <!-- Adresse -->
                <h4 class="mt-5 mb-2">{{ $t('adresse') }}</h4>
                <v-row dense>
                    <v-col cols="12" lg="4" xl="5">
                        <LabelField :label="$t('strasse')">
                            <v-text-field
                                :value="record.street"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="
                                    [streetFieldRequired(record) ? rules.required : null, rules.maxLength(100)].filter(
                                        Boolean,
                                    )
                                "
                                @input="onInput('street', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="2" xl="1">
                        <LabelField :label="$t('strasse_nr')">
                            <v-text-field
                                :value="record.street_nr"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.maxLength(15)]"
                                @input="onInput('street_nr', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('zusatz')">
                            <v-text-field
                                :value="record.address_supplement"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.maxLength(100)]"
                                @input="onInput('address_supplement', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <!-- plz/ort -->
                <v-row dense>
                    <v-col cols="12" lg="2">
                        <LabelField :label="$t('plz')">
                            <v-text-field
                                :value="record.zip"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="
                                    [
                                        zipFieldRequired(record) ? rules.required : null,
                                        rules.between(1000, 99999),
                                    ].filter(Boolean)
                                "
                                @input="onInput('zip', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="4">
                        <LabelField :label="$t('ort')">
                            <v-text-field
                                :value="record.city"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="
                                    [cityFieldRequired(record) ? rules.required : null, rules.maxLength(100)].filter(
                                        Boolean,
                                    )
                                "
                                @input="onInput('city', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('postfach')">
                            <v-text-field
                                :value="record.po_box"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.maxLength(100)]"
                                @input="onInput('po_box', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <!-- land -->
                <v-row dense>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('land')">
                            <CountrySelectField
                                :value="record.country_id"
                                hide-details="auto"
                                item-value="id"
                                :label="null"
                                @input="onInput('country_id', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6"></v-col>
                </v-row>

                <!-- Location specific -->
                <h4 v-if="locationRec" class="mt-5 mb-2">{{ $t('zusatzinfos') }}</h4>
                <v-row v-if="locationRec" dense>
                    <v-col cols="12" lg="6">
                        <LabelField
                            :label="$t('opening_hours')"
                            :label-tip="$t('location_edit_opening_hours_label_tip')"
                        >
                            <v-textarea
                                :value="locationRec.opening_hours"
                                dense
                                rows="8"
                                outlined
                                hide-details="auto"
                                :counter="250"
                                :rules="[rules.maxLength(250)]"
                                @input="locationRec.set('opening_hours', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('accessibility')" :label-tip="$t('accessibility_label_location_tip')">
                            <v-textarea
                                :value="locationRec.accessibility"
                                :placeholder="$t('placeholder_accessibility_location')"
                                dense
                                outlined
                                hide-details="auto"
                                :counter="1000"
                                :rules="[rules.maxLength(1000)]"
                                @input="locationRec.set('accessibility', $event)"
                            />
                        </LabelField>
                        <LabelField
                            :label="$t('accessibility_link_location')"
                            :label-tip="$t('accessibility_link_location_tip')"
                        >
                            <AccessibilityUrlField
                                :location-id="locationRec.id"
                                :accessibility-url="locationRec.accessibility_link"
                                @input="locationRec.set('accessibility_link', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <h4 class="mt-5 mb-2">{{ $t('contact') }}</h4>
                <!-- tel -->
                <v-row dense>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('tel_1')">
                            <v-text-field
                                :value="record.tel"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.maxLength(50)]"
                                @input="onInput('tel', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('tel_2')">
                            <v-text-field
                                :value="record.mobile"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.maxLength(50)]"
                                @input="onInput('mobile', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <!-- mail -->
                <v-row dense>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('email')">
                            <v-text-field
                                :value="record.email"
                                dense
                                outlined
                                hide-details="auto"
                                :rules="[rules.email, rules.maxLength(255)]"
                                @input="onInput('email', $event)"
                            >
                                <template #append>
                                    <a :href="`mailto:${record.email}`">
                                        <KpIcon icon-cls="link" color="primary" disabled-color="grey" />
                                    </a>
                                </template>
                            </v-text-field>
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('webseite')">
                            <UrlField
                                :value="record.web"
                                :rules="[rules.maxLength(255)]"
                                @input="onInput('web', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <!-- EventOrganizer specific -->
                <div v-if="eventOrganizerRec && record.legal_type === 'organization'">
                    <v-row dense class="mt-5">
                        <v-col cols="12">
                            <LabelField
                                :label="$t('vorverkauf')"
                                :label-tip="$t('vorverkauf_tip')"
                                label-style="font-weight:500;font-size:1.15rem;color:rgba(0,0,0,0.87)"
                            >
                                <v-textarea
                                    :value="eventOrganizerRec.standard_text_tickets"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    counter
                                    @input="eventOrganizerRec.set('standard_text_tickets', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense class="mt-5">
                        <v-col cols="12">
                            <LabelField
                                :label="$t('preise_kosten')"
                                :label-tip="$t('placeholder_event_additional_info_ticketprices')"
                                label-style="font-weight:500;font-size:1.15rem;color:rgba(0,0,0,0.87)"
                            >
                                <v-textarea
                                    :value="eventOrganizerRec.ticketprices"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    counter
                                    @input="eventOrganizerRec.set('ticketprices', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row dense class="mt-5">
                        <v-col cols="12">
                            <LabelField
                                :label="$t('activate_reduction')"
                                label-style="font-weight:500;font-size:1.15rem;color:rgba(0,0,0,0.87)"
                            >
                                <BannerReductionSelect
                                    :value="eventOrganizerRec.active_reduction"
                                    @input="eventOrganizerRec.set('active_reduction', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                    <!-- Default Location -->
                    <v-row dense class="my-5">
                        <v-col cols="12">
                            <LabelField :label="$t('default_location')">
                                <LocationSelectField
                                    :location-id="eventOrganizerRec.default_location_id"
                                    :organizer-id="eventOrganizerRec.id"
                                    :show-all-locations="false"
                                    @selected="onDefaultLocationSelect"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                </div>

                <!-- Zusagedatum Infomail -->
                <v-row v-if="record.legal_type === 'person' && globalParam('address.show_infomail')" dense>
                    <v-col cols="12" lg="6">
                        <LabelField
                            :label="`${$t('zusagedatum_infomail')}: ${record.infomail_tgk_date.formatIf(
                                'dd, DD.MM.YYYY',
                                '-',
                            )}`"
                            class="mt-2"
                        >
                            <v-checkbox
                                :input-value="record.infomail_tgk"
                                :label="$t('infomails_erhalten')"
                                @change="onInput('infomail_tgk', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <div v-if="userIsStaff()">
                    <h4 class="mt-5">
                        <em>{{ $t('intern') }}</em>
                    </h4>
                    <v-row dense>
                        <v-col cols="12" lg="6">
                            <v-checkbox
                                dense
                                :input-value="record.is_active"
                                :label="$t('aktiver_autor')"
                                @change="onInput('is_active', $event)"
                            />
                            <v-checkbox
                                dense
                                :input-value="record.is_partner"
                                :label="$t('kulturpartner')"
                                @change="onInput('is_partner', $event)"
                            />
                        </v-col>

                        <v-col cols="12" lg="6">
                            <LabelField :label="$t('mailing_listen')">
                                <MailingListMultiselect
                                    :value="record.mailing_list_ids"
                                    :placeholder="$t('choose_multi')"
                                    @change="record.set('mailing_list_ids', $event)"
                                />
                            </LabelField>
                        </v-col>
                    </v-row>
                </div>
            </v-form>

            <div class="d-flex justify-end">
                <ActionBtn :disabled="!value" @click="$emit('save-request')">
                    <KpIcon icon-cls="calendar" color="white" disabled-color="grey" left />
                    {{ $t('save_addr') }}
                </ActionBtn>
            </div>

            <hr class="my-5" />

            <div class="d-flex flex-column align-end">
                <PlainBtn :disabled="!record.id" @click="onAddNewContactBtn">
                    <span class="kontakt-link">{{ $t('add_new_contact') }}</span>
                    <KpIcon icon-cls="user" color="primary" />
                </PlainBtn>
                <PlainBtn :disabled="!record.id" @click="showAddExistingContactDlg = true">
                    <span class="kontakt-link">{{ $t('new_addr_connect') }}</span>
                    <KpIcon icon-cls="link" color="primary" />
                </PlainBtn>
                <PlainBtn
                    v-if="specificRecord && record.type === 'event_organizer'"
                    :disabled="!record.id"
                    @click="showAddExistingLocationDlg = true"
                >
                    <span class="kontakt-link">{{ $t('new_loc_connect') }}</span>
                    <KpIcon icon-cls="link" color="primary" />
                </PlainBtn>
            </div>
        </v-container>

        <AssignedAddressesPanel
            ref="assignedAddressesPanel"
            class="kontakt-content"
            :record="record"
            :specific-record="specificRecord"
            @saving="$emit('saving', $event)"
            @assign-event-organizer="$emit('assign-event-organizer', $event)"
        />

        <LinkExistingContactDlg v-model="showAddExistingContactDlg" @select-address="onLinkContact" />
        <LinkExistingLocationDlg
            v-model="showAddExistingLocationDlg"
            :event-organizer="specificRecord"
            @select-location="onLinkLocation"
        />
        <SimpleAddressDlg
            v-if="newAssignedAddress"
            v-model="showNewAssignedAddressDlg"
            :record="newAssignedAddress"
            :loading="loading"
            @save-btn="onNewAssignedAddrSave"
        >
            <template #after-form>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="$t('func_info')">
                            <v-textarea
                                v-model="newContactFunction"
                                dense
                                outlined
                                rows="3"
                                placeholder="z.B. 'Veranstaltungsleiterin'"
                            ></v-textarea>
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <v-checkbox
                            class="mt-0 pt-0"
                            :input-value="publishFrontend"
                            :label="$t('frontend_publish_contact')"
                            hide-details="auto"
                            @change="publishFrontend = $event"
                        >
                            <template #label>
                                <KpIcon left icon-cls="published" color="grey" />
                                <span v-html="$t('frontend_publish_contact')"></span>
                            </template>
                        </v-checkbox>
                    </v-col>
                </v-row>
                <v-row v-if="userIsStaff()" dense>
                    <v-col cols="12" class="d-flex">
                        <v-checkbox
                            class="mt-0 pt-0"
                            :input-value="isMainContact"
                            :label="$t('main_contact_msgs')"
                            hide-details="auto"
                            @change="isMainContact = $event"
                        >
                            <template #label>
                                <KpIcon left icon-cls="star" color="grey" />
                                <span v-html="$t('main_contact_msgs')"></span>
                            </template>
                        </v-checkbox>
                        <KpTooltip>
                            <template #activator="{ on, attrs }">
                                <i v-bind="attrs" class="ic ic-16 ic-col-light-blue ic-support ml-1" v-on="on"></i>
                            </template>
                            <div style="max-width: 350px">asfdsfsdfdfs</div>
                        </KpTooltip>
                    </v-col>
                </v-row>
            </template>
        </SimpleAddressDlg>
    </div>
</template>

<script>
import { formRules, errToStr } from '@/helpers/tools';
import { Address } from '@/models/Address';
import { AddressContact } from '@/models/AddressContact';
import { Location } from '@/models/Location';
import { EventOrganizer } from '@/models/EventOrganizer';
import GlobalsMixin from '@/mixins/globals';
import { userIsStaff, userIsOrganizer } from '@/helpers/auth';
import CountrySelectField from '@/components/country/SelectField.vue';
import PublishedIndicator from '@/components/address/PublishedIndicator.vue';
import AssignedAddressesPanel from '@/components/address/AssignedAddressesPanel.vue';
import MailingListMultiselect from '@/components/mailing_list/multiselect.vue';
import LinkExistingContactDlg from '@/components/address/LinkExistingContactDlg.vue';
import LinkExistingLocationDlg from '@/components/address/LinkExistingLocationDlg.vue';
import SimpleAddressDlg from '@/components/address/SimpleAddressDlg.vue';
import LocationSelectField from '@/components/location/SelectField.vue';
import BannerReductionSelect from '@/components/banner/banner-reduction-select.vue';
import AccessibilityUrlField from '@/components/form/AccessibilityUrlField.vue';

export default {
    name: 'AddressEditStammdaten',
    components: {
        CountrySelectField,
        PublishedIndicator,
        AssignedAddressesPanel,
        MailingListMultiselect,
        LinkExistingContactDlg,
        LinkExistingLocationDlg,
        SimpleAddressDlg,
        LocationSelectField,
        BannerReductionSelect,
        AccessibilityUrlField,
    },
    mixins: [GlobalsMixin],
    props: {
        record: {
            type: Address,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        specificRecord: {
            type: [Location, EventOrganizer],
            default: null,
        },
        // used to indicate form validity
        value: Boolean,
    },
    data() {
        return {
            showAddExistingContactDlg: false,
            showAddExistingLocationDlg: false,
            showNewAssignedAddressDlg: false,

            newAssignedAddress: null,
            newContactFunction: null,
            isMainContact: null,
            publishFrontend: false,
        };
    },
    computed: {
        eventOrganizerRec() {
            if (this.specificRecord instanceof EventOrganizer) {
                return this.specificRecord;
            } else {
                return null;
            }
        },
        locationRec() {
            if (this.specificRecord instanceof Location) {
                return this.specificRecord;
            } else {
                return null;
            }
        },
    },
    created() {
        this.rules = formRules;
    },
    methods: {
        userIsStaff,
        userIsOrganizer,
        // can / will be called from the parent component to initiate a forceful form validation
        validateForm() {
            this.$refs.form.validate();
        },
        onInput(field, value) {
            this.record.set(field, value);
        },

        streetFieldRequired() {
            return false;
        },
        zipFieldRequired(address) {
            // ZIP is only required for locations:
            if (address?.type === 'location') {
                return true;
            } else {
                return false;
            }
        },
        cityFieldRequired(address) {
            // city is only required for locations:
            if (address?.type === 'location') {
                return true;
            } else {
                return false;
            }
        },

        async onLinkContact({ address, functionText, isMainContact, publishFrontend }) {
            this.showAddExistingContactDlg = false;
            if (this.record && this.record.id > 0 && address) {
                const contact = new AddressContact({
                    main_address_id: this.record.id,
                    contact_address_id: address.id,
                    relation_function: functionText,
                    is_main_contact: isMainContact,
                    publish_frontend: publishFrontend,
                });
                try {
                    this.$emit('saving', true);
                    await contact.save();
                    this.$refs.assignedAddressesPanel.reloadAssignedAddresses();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                }
            }
        },

        async onLinkLocation({ location }) {
            this.showAddExistingLocationDlg = false;
            if (this.record?.type === 'event_organizer' && this.specificRecord?.id > 0 && location) {
                location.event_organizer_id = this.specificRecord.id;
                try {
                    this.$emit('saving', true);
                    await location.save();
                    this.$refs.assignedAddressesPanel.reloadAssignedLocations();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.$emit('saving', false);
                }
            }
        },

        onAddNewContactBtn() {
            const newAddr = new Address({
                typ: 'address',
            });
            this.newAssignedAddress = newAddr;
            this.showNewAssignedAddressDlg = true;
        },

        async onNewAssignedAddrSave(address) {
            try {
                this.$emit('saving', true);
                await address.save();
                const contact = new AddressContact({
                    main_address_id: this.record.id,
                    contact_address_id: address.id,
                    relation_function: this.newContactFunction,
                    is_main_contact: this.isMainContact,
                    publish_frontend: this.publishFrontend,
                });
                await contact.save();
                this.$refs.assignedAddressesPanel.reloadAssignedAddresses();
                this.showNewAssignedAddressDlg = false;
                this.newAssignedAddress = null;
                this.newContactFunction = null;
                this.isMainContact = null;
                this.publishFrontend = false;
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.$emit('saving', false);
            }
        },

        onDefaultLocationSelect(record) {
            if (record) {
                this.eventOrganizerRec?.set('default_location_id', record.id);
            } else {
                this.eventOrganizerRec?.set('default_location_id', null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.stammdaten-container {
    display: grid;
    grid-template-columns: minmax(450px, 1fr) minmax(400px, auto);
    grid-template-areas: 'form kontakte';
}

.form-content {
    grid-area: form;
    overflow: auto;
}

.kontakt-content {
    grid-area: kontakte;
    background-color: var(--toolbar-bg-color);
    overflow: auto;
}

.kontakt-link {
    color: var(--primary-color);
}

.looks-like-h4 {
    font-weight: bold;
    font-size: 1.25rem;
}
</style>
